import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import { Copy, Check, ShieldCheck, ArrowLeft, ClipboardCheck, CircleDollarSign, Hash, User2, PartyPopper, QrCode, XCircle as XCircleIcon } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CopyField = ({ label, value, icon: Icon, testid, highlight }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(String(value));
      setCopied(true);
      toast.success(`${label} copied`);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('Could not copy — please copy manually');
    }
  };
  return (
    <div className={`flex items-center justify-between rounded-xl border px-4 py-3 ${highlight ? 'border-[#00F0FF]/40 bg-[#00F0FF]/5' : 'border-[#1E2532] bg-[#0B0F17]'}`}>
      <div className="min-w-0">
        <p className="text-[#6B7280] text-[11px] uppercase tracking-wider mb-0.5 flex items-center gap-1.5">
          {Icon && <Icon className="w-3.5 h-3.5" />} {label}
        </p>
        <p className={`font-mono truncate ${highlight ? 'text-[#00F0FF] text-lg font-semibold' : 'text-white text-base'}`} data-testid={`${testid}-value`}>
          {value}
        </p>
      </div>
      <button
        onClick={copy}
        className="ml-3 flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#111621] border border-[#1E2532] hover:border-[#00F0FF]/40 text-[#A1A7B5] hover:text-white text-xs font-medium transition-colors"
        data-testid={`${testid}-copy`}
        type="button"
      >
        {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
};

const ZellePaymentPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const state = location.state || {};

  const qpOrderId = searchParams.get('order_id');
  const qpEmail = searchParams.get('email');
  const linkBased = !state.orderId && !!qpOrderId;

  const [zelle, setZelle] = useState({
    recipient: state.zelleRecipient || '',
    name: state.zelleName || 'Renew Labs LLC',
  });
  const [payInfo, setPayInfo] = useState(null);
  const [loading, setLoading] = useState(linkBased);
  const [loadError, setLoadError] = useState('');
  const [confirmationNumber, setConfirmationNumber] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const orderId = state.orderId || qpOrderId;
  const orderNumber = state.orderNumber || payInfo?.order_number;
  const amount = typeof state.amount === 'number' ? state.amount : payInfo?.amount_due;

  useEffect(() => {
    if (!orderId) {
      navigate('/products', { replace: true });
      return;
    }
    if (linkBased) {
      if (!qpEmail) { setLoadError('This payment link is missing its email. Please use the link from your email.'); setLoading(false); return; }
      setLoading(true);
      axios.get(`${API}/payments/order-pay-info`, { params: { order_id: qpOrderId, email: qpEmail } })
        .then(({ data }) => {
          setPayInfo(data);
          setZelle({ recipient: data.zelle_recipient, name: data.zelle_name });
        })
        .catch((err) => setLoadError(err.response?.data?.detail || 'Could not load this order.'))
        .finally(() => setLoading(false));
    } else if (!zelle.recipient) {
      axios.get(`${API}/payments/zelle-info`)
        .then(({ data }) => setZelle({ recipient: data.recipient, name: data.name }))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const amountStr = typeof amount === 'number' ? amount.toFixed(2) : amount;

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/payments/zelle/confirm`, {
        order_id: orderId,
        confirmation_number: confirmationNumber.trim() || null,
        email: qpEmail || state.customerEmail || null,
      });
      setDone(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not submit your payment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!orderId) return null;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-[#00F0FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4 py-16">
        <div className="glass-card p-8 max-w-lg w-full text-center" data-testid="zelle-link-error">
          <h1 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Link Problem</h1>
          <p className="text-[#A1A7B5] mb-6">{loadError}</p>
          <Button onClick={() => navigate('/products')} className="btn-primary" data-testid="zelle-error-shop-btn">Go to Shop</Button>
        </div>
      </div>
    );
  }

  if (payInfo?.already_paid) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4 py-16">
        <div className="glass-card p-8 max-w-lg w-full text-center" data-testid="zelle-already-paid">
          <div className="w-16 h-16 rounded-full bg-green-500/15 flex items-center justify-center mx-auto mb-5">
            <ShieldCheck className="w-8 h-8 text-green-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Already Paid</h1>
          <p className="text-[#A1A7B5] mb-6">Order <span className="text-[#00F0FF] font-mono">{orderNumber}</span> is already paid and being processed — no further payment needed.</p>
          <Button onClick={() => navigate('/track')} className="btn-primary" data-testid="zelle-alreadypaid-track-btn">Track My Order</Button>
        </div>
      </div>
    );
  }

  if (payInfo && !payInfo.payable) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4 py-16">
        <div className="glass-card p-8 max-w-lg w-full text-center" data-testid="zelle-not-payable">
          <div className="w-16 h-16 rounded-full bg-red-500/15 flex items-center justify-center mx-auto mb-5">
            <XCircleIcon className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Payment Unavailable</h1>
          <p className="text-[#A1A7B5] mb-6">Order <span className="text-[#00F0FF] font-mono">{orderNumber}</span> can no longer be paid — it may have been cancelled. Please contact us at <a href="mailto:support@getrenewlabs.com" className="text-[#00F0FF]">support@getrenewlabs.com</a> and we&apos;ll help.</p>
          <Button onClick={() => navigate('/products')} className="btn-primary" data-testid="zelle-notpayable-shop-btn">Go to Shop</Button>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4 py-16">
        <div className="glass-card p-8 sm:p-10 max-w-lg w-full text-center" data-testid="zelle-success">
          <div className="w-16 h-16 rounded-full bg-green-500/15 flex items-center justify-center mx-auto mb-5">
            <PartyPopper className="w-8 h-8 text-green-400" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Order Received!
          </h1>
          <p className="text-[#A1A7B5] mb-6 leading-relaxed">
            Thanks! We&apos;ve got your order <span className="text-[#00F0FF] font-mono font-semibold">{orderNumber}</span>.
            We&apos;re verifying your Zelle payment and will email your confirmation as soon as it clears
            (usually within 1 business day). Your order ships right after that.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={() => navigate('/track')} className="btn-primary" data-testid="zelle-track-order-btn">
              Track My Order
            </Button>
            <Button onClick={() => navigate('/products')} variant="outline" className="border-[#1E2532] text-[#A1A7B5] hover:text-white" data-testid="zelle-continue-shopping-btn">
              Continue Shopping
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] px-4 py-10 sm:py-14">
      <div className="max-w-2xl mx-auto">
        <Link to="/checkout" className="inline-flex items-center text-[#A1A7B5] hover:text-white text-sm mb-6 transition-colors" data-testid="zelle-back-link">
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Checkout
        </Link>

        <div className="glass-card p-6 sm:p-8" data-testid="zelle-payment-page">
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Complete Your Payment with Zelle
          </h1>
          <p className="text-[#A1A7B5] mb-7 leading-relaxed">
            Your order is <span className="text-white">reserved</span>. Just follow the 4 steps below. The most important part: <span className="text-white">put your order number in the Zelle memo</span> so we can match your payment and ship fast.
          </p>

          {/* Scan-to-pay QR (fastest path) */}
          <div className="rounded-xl border border-[#00F0FF]/30 bg-[#0B0F17] p-5 mb-7" data-testid="zelle-qr-block">
            <p className="text-white font-semibold mb-1 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <QrCode className="w-4 h-4 text-[#00F0FF]" /> Fastest: scan to pay
            </p>
            <p className="text-[#A1A7B5] text-sm mb-4">
              Open your bank app → <span className="text-white">Zelle</span> → <span className="text-white">Scan a QR code</span>, then scan (or screenshot) this. It fills in <span className="text-green-400 font-medium">{zelle.name}</span> automatically — you just enter the amount &amp; memo below.
            </p>
            <div className="flex justify-center">
              <div className="bg-white rounded-xl p-3">
                <img
                  src="/zelle-qr.jpeg"
                  alt="Zelle QR code to pay Renew Labs LLC (billing-renewlabs)"
                  className="w-56 h-auto block"
                  data-testid="zelle-qr-image"
                />
              </div>
            </div>
            <p className="text-[#6B7280] text-xs text-center mt-3">
              After scanning, enter <span className="text-white">${amountStr}</span> and put <span className="text-white font-mono">{orderNumber}</span> in the memo.
            </p>
          </div>

          {/* The key values */}
          <div className="space-y-3 mb-7">
            <CopyField label="Amount to Send (exact)" value={`$${amountStr}`} icon={CircleDollarSign} testid="zelle-amount" highlight />
            <CopyField label="Zelle ID — send to this" value={zelle.recipient} icon={User2} testid="zelle-recipient" />
            <div className="flex items-center gap-2 -mt-1 pl-1 text-xs text-[#A1A7B5]" data-testid="zelle-recipient-name">
              <User2 className="w-3.5 h-3.5 text-green-400" />
              Recipient should show as <span className="text-green-400 font-medium">{zelle.name}</span> — that&apos;s how you know it&apos;s us.
            </div>
            <CopyField label="Payment Note / Memo — REQUIRED" value={orderNumber} icon={Hash} testid="zelle-order-number" highlight />
          </div>

          {/* Step-by-step */}
          <div className="rounded-xl border border-[#1E2532] bg-[#0B0F17] p-5 mb-7">
            <p className="text-white font-semibold mb-3 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <ClipboardCheck className="w-4 h-4 text-[#00F0FF]" /> How to pay — step by step
            </p>
            <ol className="space-y-3 text-sm text-[#A1A7B5]">
              <li className="flex gap-3">
                <span className="text-[#00F0FF] font-semibold">1.</span>
                <span>Open your bank&apos;s app and go to <span className="text-white">Zelle</span> → <span className="text-white">Send Money</span>. Choose <span className="text-white">Add a recipient / new contact</span>.</span>
              </li>
              <li className="flex gap-3">
                <span className="text-[#00F0FF] font-semibold">2.</span>
                <span>In the recipient field, enter our Zelle ID: <span className="text-white font-mono">{zelle.recipient}</span>. Confirm it comes up as <span className="text-green-400 font-medium">{zelle.name}</span> before continuing.</span>
              </li>
              <li className="flex gap-3">
                <span className="text-[#00F0FF] font-semibold">3.</span>
                <span>Enter the exact amount <span className="text-white font-semibold">${amountStr}</span> and send.</span>
              </li>
              <li className="flex gap-3">
                <span className="text-[#00F0FF] font-semibold">4.</span>
                <span>In the <span className="text-white">note / memo</span> field, type your order number <span className="text-white font-mono">{orderNumber}</span>. <span className="text-white">This is required</span> — it&apos;s how we match your payment to your order.</span>
              </li>
            </ol>
            <div className="mt-4 rounded-lg bg-[#00F0FF]/5 border border-[#00F0FF]/20 px-3 py-2.5 text-xs text-[#A1A7B5] leading-relaxed">
              <span className="text-white">Large order?</span> Zelle daily limits vary by bank. If your payment is blocked, you can split it across two sends (or two days) using the same order number in the memo — or email us and we&apos;ll help.
            </div>
          </div>

          {/* Confirmation number (optional) */}
          <div className="mb-6">
            <label className="text-[#A1A7B5] text-sm mb-2 block">
              Zelle confirmation / reference number <span className="text-[#6B7280]">(optional — only if your bank shows one)</span>
            </label>
            <Input
              value={confirmationNumber}
              onChange={(e) => setConfirmationNumber(e.target.value)}
              placeholder="Paste it here if you see one — otherwise leave blank"
              className="bg-[#0B0F17] border-[#1E2532] text-white"
              data-testid="zelle-confirmation-input"
            />
            <p className="text-[#6B7280] text-xs mt-2 leading-relaxed">
              Most banks don&apos;t show the sender a confirmation number — that&apos;s totally fine. We match your payment using your order number and the exact amount.
            </p>
          </div>

          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="btn-primary w-full py-4 text-lg"
            data-testid="zelle-submit-btn"
          >
            {submitting ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                Submitting...
              </span>
            ) : (
              "I've Sent Payment — Complete Order"
            )}
          </Button>

          <p className="text-[#6B7280] text-xs mt-4 flex items-start gap-2 leading-relaxed">
            <ShieldCheck className="w-4 h-4 text-[#00F0FF] flex-shrink-0 mt-0.5" />
            We verify every Zelle payment against our bank before shipping. Once confirmed, you&apos;ll get an order confirmation email and your package ships within 1–2 business days.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ZellePaymentPage;
